import { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import ReviewBox, { VerifiedBadge } from './ReviewBox';
import { t } from '../i18n';
import PiPrice from './PiPrice';

const API_BASE = '/api/academy';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}

const colors = {
  bg: '#F7F5F0', surface: '#fff', border: '#E3E1D9', text: '#1F1E1B',
  textMuted: '#5F5E5A', primary: '#0F6E56', primaryBg: '#E1F5EE', danger: '#A32D2D',
};

const CATEGORIES = [
  ['development', 'Development'], ['business', 'Business'], ['design', 'Design'],
  ['marketing', 'Marketing'], ['it_software', 'IT & Software'], ['personal_dev', 'Personal Development'],
  ['language', 'Language'], ['music', 'Music'], ['other', 'Other'],
];

function AcademyHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('ach_title')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_what_q')}</p>
          <p style={textStyle}>{t('ach_what')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('ach_learn_q')}</p>
          <p style={textStyle}>{t('ach_l1')}</p>
          <p style={textStyle}>{t('ach_l2')}</p>
          <p style={textStyle}>{t('ach_l3')}</p>
          <p style={textStyle}>{t('ach_l4')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('ach_teach_q')}</p>
          <p style={textStyle}>{t('ach_t1')}</p>
          <p style={textStyle}>{t('ach_t2')}</p>
          <p style={textStyle}>{t('ach_t3')}</p>
          <p style={textStyle}>{t('ach_t4')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('ach_fees_q')}</p>
          <p style={textStyle}>{t('ach_fees')}</p>
        </div>
        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('got_it')}</button>
      </div>
    </div>
  );
}

function CourseCard({ course, onOpen }) {
  return (
    <div onClick={() => onOpen(course.slug)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}>
      <div style={{ width: '100%', aspectRatio: '16/9', background: '#F1EFE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {course.cover_image ? <img src={course.cover_image} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 32, opacity: 0.3 }}>🎓</span>}
      </div>
      <div style={{ padding: '0.65rem' }}>
        {course.is_boosted && <span style={{ fontSize: 10, background: '#FFF3C4', color: '#8A6D00', padding: '2px 6px', borderRadius: 8, marginBottom: 4, display: 'inline-block' }}>{t('lbl_sponsored')}</span>}
        {course.matched_interest && <span style={{ fontSize: 10, background: colors.primaryBg, color: colors.primary, padding: '2px 6px', borderRadius: 8, marginBottom: 4, marginLeft: 4, display: 'inline-block' }}>{t('lbl_for_you')}</span>}
        <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 4px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{course.title}</p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 4px' }}>@{course.instructor_username} · {course.lessons_count} lessons</p>
        {course.avg_rating > 0 && <span style={{ fontSize: 11, color: colors.textMuted }}>⭐ {course.avg_rating} ({course.total_reviews})</span>}
        <p style={{ fontSize: 14, fontWeight: 600, color: colors.primary, margin: '4px 0 0' }}>{course.price_pi > 0 ? `$<PiPrice pi={course.price_pi} />` : 'Free'}</p>
      </div>
    </div>
  );
}

function CourseDetailModal({ slug, onClose, onEnrolled }) {
  const [course, setCourse] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch(`${API_BASE}/courses/${slug}/`, { headers: getAuthToken() ? authHeaders() : {} }).then((r) => r.json()).then(setCourse).catch(() => {});
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const doEnroll = async () => {
    if (!getAuthToken()) { setMsg(t('login_first')); return; }
    setEnrolling(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/courses/${course.id}/enroll/`, { method: 'POST', headers: authHeaders() });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not enroll.');

      if (!data.requires_payment) {
        setMsg("You're enrolled!");
        setEnrolling(false);
        load();
        onEnrolled();
        return;
      }

      if (!window.Pi) { setMsg(t('open_pi_pay')); setEnrolling(false); return; }
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: data.price_pi, memo: `Enroll: ${course.title}`, metadata: { enrollment_id: data.enrollment_id } },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/enrollments/${data.enrollment_id}/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/enrollments/${data.enrollment_id}/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid }) })
              .then(() => { setMsg("Payment complete - you're enrolled!"); setEnrolling(false); load(); onEnrolled(); })
              .catch(() => { setMsg('Payment reached Pi but enrollment failed.'); setEnrolling(false); });
          },
          onCancel: () => { setMsg(t('pay_cancelled')); setEnrolling(false); },
          onError: () => { setMsg(t('pay_error')); setEnrolling(false); },
        }
      );
    } catch (e) { setMsg(e.message); setEnrolling(false); }
  };

  const markComplete = async (lessonId) => {
    try {
      await fetch(`${API_BASE}/lessons/${lessonId}/complete/`, { method: 'POST', headers: authHeaders() });
      load();
    } catch (e) {}
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        {!course ? <p style={{ textAlign: 'center', color: colors.textMuted }}>{t('loading')}</p> : (
          <>
            {course.cover_image && <img src={course.cover_image} alt={course.title} style={{ width: '100%', borderRadius: 10, marginBottom: 12 }} />}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>{course.title}</h2>
              <VerifiedBadge module="academy" objectId={course.id || course.slug} />
            </div>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 12px' }}>By @{course.instructor_username}{course.instructor_title ? ` - ${course.instructor_title}` : ''} · {course.students_count} students</p>
            <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>{course.description}</p>
            <ReviewBox
              module="academy"
              objectId={course.id || course.slug}
              objectTitle={course.title}
              isAdmin={localStorage.getItem('username') === 'khalidxpert'}
            />

            {course.is_enrolled && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ background: colors.primaryBg, borderRadius: 8, height: 8, overflow: 'hidden' }}>
                  <div style={{ width: `${course.progress_percent}%`, background: colors.primary, height: '100%' }} />
                </div>
                <p style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>{course.progress_percent}% complete</p>
              </div>
            )}

            <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{t('lessons')}</p>
            {course.lessons.map((l) => (
              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `0.5px solid ${colors.border}` }}>
                <div>
                  <p style={{ fontSize: 13, margin: 0 }}>{l.title} {l.is_preview && '(preview)'}</p>
                  <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{l.duration_minutes} min</p>
                  {l.video_url && <a href={l.video_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: colors.primary }}>{t('ac_watch')}</a>}
                </div>
                {course.is_enrolled && (
                  <button onClick={() => markComplete(l.id)} style={{ fontSize: 11, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer' }}>{t('ac_done')}</button>
                )}
              </div>
            ))}

            {!course.is_enrolled && (
              <>
                <p style={{ fontSize: 16, fontWeight: 600, color: colors.primary, margin: '16px 0 12px' }}>{course.price_pi > 0 ? <PiPrice pi={course.price_pi} /> : 'Free'}</p>
                <button onClick={doEnroll} disabled={enrolling} style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
                  {enrolling ? 'Processing...' : course.price_pi > 0 ? `Enroll - $<PiPrice pi={course.price_pi} />` : 'Enroll for free'}
                </button>
              </>
            )}
            {msg && <p style={{ fontSize: 12, color: colors.danger, marginTop: 8 }}>{msg}</p>}

            {course.reviews?.length > 0 && (
              <div style={{ marginTop: 16, borderTop: `0.5px solid ${colors.border}`, paddingTop: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{t('reviews')}</p>
                {course.reviews.map((r, i) => (
                  <p key={i} style={{ fontSize: 12, color: colors.textMuted, margin: '4px 0' }}>@{r.reviewer} {'⭐'.repeat(r.rating)} {r.comment}</p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CreateCourseForm({ onCreated }) {
  const [form, setForm] = useState({ title: '', category: 'other', level: 'beginner', description: '', price_pi: 0 });
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/courses/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not create course.');
      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        const token = getAuthToken();
        await fetch(`${API_BASE}/courses/${data.id}/upload-image/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      }
      alert('Course created! Now add lessons from "My Courses".');
      setForm({ title: '', category: 'other', level: 'beginner', description: '', price_pi: 0 });
      onCreated();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  return (
    <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 12px' }}>{t('create_course')}</p>
      <div style={{ display: 'grid', gap: 10 }}>
        <div><label style={labelStyle}>{t('title_label')}</label><input style={inputStyle} value={form.title} onChange={update('title')} placeholder="Complete React Development Bootcamp" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('category_label')}</label>
            <select style={inputStyle} value={form.category} onChange={update('category')}>
              {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>{t('level_label')}</label>
            <select style={inputStyle} value={form.level} onChange={update('level')}>
              <option value="beginner">{t('beginner')}</option><option value="intermediate">{t('intermediate')}</option><option value="advanced">{t('advanced')}</option>
            </select>
          </div>
        </div>
        <div><label style={labelStyle}>{t('description')}</label><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={form.description} onChange={update('description')} /></div>
        <div><label style={labelStyle}>{t('price_pi_free')}</label><input type="number" step="0.01" style={inputStyle} value={form.price_pi} onChange={update('price_pi')} /></div>
        <div><label style={labelStyle}>{t('cover_image')}</label><input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} /></div>
      </div>
      <button onClick={submit} disabled={submitting || !form.title} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 12 }}>
        {submitting ? 'Creating...' : 'Create course'}
      </button>
    </div>
  );
}

function MyCoursesPanel() {
  const [courses, setCourses] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [lessonForm, setLessonForm] = useState({ title: '', video_url: '', content: '', duration_minutes: 10, is_preview: false });
  const [boosting, setBoosting] = useState({});

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/courses/mine/`, { headers: authHeaders() });
    if (res.ok) setCourses((await safeJson(res)).courses);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addLesson = async (courseId) => {
    try {
      await fetch(`${API_BASE}/courses/${courseId}/lessons/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(lessonForm) });
      setLessonForm({ title: '', video_url: '', content: '', duration_minutes: 10, is_preview: false });
      load();
    } catch (e) { alert('Could not add lesson.'); }
  };

  const boostCourse = async (id, title) => {
    if (!window.Pi) { alert(t('open_pi_pay2')); return; }
    setBoosting((b) => ({ ...b, [id]: true }));
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: 1, memo: `Boost course: ${title}`, metadata: { course_id: id } },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/courses/${id}/boost/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/courses/${id}/boost/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid, amount: 1, days: 7 }) })
              .then(() => { alert('Boosted!'); setBoosting((b) => ({ ...b, [id]: false })); load(); })
              .catch(() => setBoosting((b) => ({ ...b, [id]: false })));
          },
          onCancel: () => setBoosting((b) => ({ ...b, [id]: false })),
          onError: () => setBoosting((b) => ({ ...b, [id]: false })),
        }
      );
    } catch (e) { setBoosting((b) => ({ ...b, [id]: false })); }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };

  return (
    <div>
      {courses && courses.length > 0 && courses.map((c) => (
        <div key={c.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{c.title}</p>
              <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>{c.students_count} students · {c.lessons_count} lessons · {c.price_pi > 0 ? `$<PiPrice pi={c.price_pi} />` : 'Free'}</p>
            </div>
            <button onClick={() => setExpandedId(expandedId === c.id ? null : c.id)} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
              {expandedId === c.id ? 'Close' : 'Manage'}
            </button>
          </div>
          {expandedId === c.id && (
            <div style={{ marginTop: 10, borderTop: `0.5px solid ${colors.border}`, paddingTop: 10 }}>
              <button onClick={() => boostCourse(c.id, c.title)} disabled={boosting[c.id] || c.is_boosted} style={{ width: '100%', padding: '6px', fontSize: 12, fontWeight: 500, border: 'none', borderRadius: 4, background: c.is_boosted ? '#FFF3C4' : colors.primaryBg, color: c.is_boosted ? '#8A6D00' : colors.primary, cursor: 'pointer', marginBottom: 10 }}>
                {c.is_boosted ? '📢 Boosted' : boosting[c.id] ? 'Processing...' : '📢 Boost (1 Pi / 7d)'}
              </button>
              <p style={{ fontSize: 12, fontWeight: 600, margin: '0 0 6px' }}>{t('add_lesson_heading')}</p>
              <div style={{ display: 'grid', gap: 6, marginBottom: 8 }}>
                <input style={inputStyle} placeholder={t('lesson_title')} value={lessonForm.title} onChange={(e) => setLessonForm((f) => ({ ...f, title: e.target.value }))} />
                <input style={inputStyle} placeholder={t('video_url')} value={lessonForm.video_url} onChange={(e) => setLessonForm((f) => ({ ...f, video_url: e.target.value }))} />
                <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} placeholder={t('notes_text_content')} value={lessonForm.content} onChange={(e) => setLessonForm((f) => ({ ...f, content: e.target.value }))} />
                <input type="number" style={inputStyle} placeholder={t('duration_min')} value={lessonForm.duration_minutes} onChange={(e) => setLessonForm((f) => ({ ...f, duration_minutes: e.target.value }))} />
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={lessonForm.is_preview} onChange={(e) => setLessonForm((f) => ({ ...f, is_preview: e.target.checked }))} /> Free preview lesson
                </label>
              </div>
              <button onClick={() => addLesson(c.id)} style={{ width: '100%', padding: '8px', fontSize: 13, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('add_lesson_btn')}</button>
            </div>
          )}
        </div>
      ))}
      <CreateCourseForm onCreated={load} />
    </div>
  );
}

function MyEnrollmentsPanel({ onOpen }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/enrollments/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((data) => { setEnrollments(data.enrollments || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('loading')}</p>;
  if (enrollments.length === 0) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_enrolled_courses')}</p>;

  return (
    <div>
      {enrollments.map((e) => (
        <div key={e.id} onClick={() => onOpen(e.course_slug)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8, cursor: 'pointer' }}>
          <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 4px' }}>{e.course_title}</p>
          <div style={{ background: colors.primaryBg, borderRadius: 8, height: 6, overflow: 'hidden' }}>
            <div style={{ width: `${e.progress_percent}%`, background: colors.primary, height: '100%' }} />
          </div>
          <p style={{ fontSize: 11, color: colors.textMuted, margin: '4px 0 0' }}>{e.progress_percent}% complete {e.completed_at && '· ✅ Completed'}</p>
        </div>
      ))}
    </div>
  );
}

function WalletPanel() {
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [showLedger, setShowLedger] = useState(false);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/wallet/balance/`, { headers: authHeaders() });
    if (res.ok) setBalance(await safeJson(res));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLedger = async () => {
    if (!showLedger) {
      const res = await fetch(`${API_BASE}/wallet/ledger/`, { headers: authHeaders() });
      if (res.ok) setLedger((await safeJson(res)).entries);
    }
    setShowLedger((s) => !s);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/wallet/withdraw/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ amount: parseFloat(amount), pi_wallet_address: walletAddress }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit request.');
      alert(t('msg_wd_submitted'));
      setAmount(''); setWalletAddress('');
      load();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  if (balance === null) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_wallet')}</p>;

  return (
    <div>
      <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>{t('available_to_withdraw')}</p>
        <p style={{ fontSize: 26, fontWeight: 600, margin: '0 0 4px', color: colors.primary }}><PiPrice pi={balance.available_balance_pi} /></p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 8px' }}>{t('earned_after_fee')} <PiPrice pi={balance.total_earned_pi} /></p>
        <button onClick={toggleLedger} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{showLedger ? 'Hide' : 'View'} ledger</button>
        {showLedger && (
          <div style={{ marginTop: 10, borderTop: `0.5px solid ${colors.border}`, paddingTop: 10 }}>
            {ledger.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted }}>{t('no_paid_enrollments')}</p> :
              ledger.map((e) => (
                <div key={e.enrollment_id} style={{ fontSize: 12, padding: '6px 0', borderBottom: `0.5px solid ${colors.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{e.course_title} · @{e.student}</span>
                    <span><PiPrice pi={e.net_pi} /> net</span>
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>
      <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 12px' }}>{t('request_withdrawal')}</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
          <input type="number" step="0.000001" placeholder={t('amount_pi')} value={amount} onChange={(e) => setAmount(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
          <input type="text" placeholder={t('your_wallet_address')} value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
        </div>
        <button onClick={submit} disabled={submitting || !amount || !walletAddress} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          {submitting ? 'Submitting...' : 'Request withdrawal'}
        </button>
      </div>
    </div>
  );
}

export default function AcademyApp() {
  const [tab, setTab] = useState('browse');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [personalized, setPersonalized] = useState(false);
  const [openSlug, setOpenSlug] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const loadCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedCategory) params.set('category', selectedCategory);
      const res = await fetch(`${API_BASE}/courses/?${params.toString()}`, { headers: getAuthToken() ? authHeaders() : {} });
      const data = await safeJson(res);
      setCourses(data.courses || []);
      setPersonalized(!!data.personalized);
    } catch (e) {} finally { setLoading(false); }
  }, [search, selectedCategory]);

  const loadCategories = useCallback(async () => {
    const res = await fetch(`${API_BASE}/courses/categories/`);
    if (res.ok) setCategories((await safeJson(res)).categories);
  }, []);

  useEffect(() => { loadCourses(); }, [loadCourses]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const tabStyle = (t) => ({ padding: '8px 14px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: tab === t ? colors.primary : colors.textMuted, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <AcademyHelpModal onClose={() => setShowHelp(false)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>{t('ac_app')}</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('academy_tagline')}</p>
        </div>
        <button onClick={() => setShowHelp(true)} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>{t('how_it_works_btn')}</button>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: `0.5px solid ${colors.border}`, marginBottom: '1.25rem' }}>
        <button style={tabStyle('browse')} onClick={() => setTab('browse')}>{t('browse')}</button>
        <button style={tabStyle('mycourses')} onClick={() => setTab('mycourses')}>{t('my_courses')}</button>
        <button style={tabStyle('enrollments')} onClick={() => setTab('enrollments')}>{t('my_learning')}</button>
        <button style={tabStyle('wallet')} onClick={() => setTab('wallet')}>{t('wallet')}</button>
      </div>

      {tab === 'browse' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem' }}>
            <input placeholder={t('ac_search_ph')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 20, boxSizing: 'border-box' }} />
            <button onClick={loadCourses} style={{ padding: '0 14px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>🔄</button>
          </div>
          {categories.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: '0.5rem' }}>
              <button onClick={() => setSelectedCategory('')} style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, borderRadius: 14, whiteSpace: 'nowrap', border: `0.5px solid ${colors.border}`, cursor: 'pointer', background: selectedCategory === '' ? colors.primary : '#fff', color: selectedCategory === '' ? '#fff' : colors.textMuted }}>{t('all_filter')}</button>
              {categories.map((c) => (
                <button key={c.name} onClick={() => setSelectedCategory(c.name)} style={{ flexShrink: 0, padding: '5px 12px', fontSize: 12, borderRadius: 14, whiteSpace: 'nowrap', border: `0.5px solid ${colors.border}`, cursor: 'pointer', background: selectedCategory === c.name ? colors.primary : '#fff', color: selectedCategory === c.name ? '#fff' : colors.textMuted }}>{c.name} ({c.count})</button>
              ))}
            </div>
          )}
          {personalized && <p style={{ fontSize: 12, color: colors.primary, margin: '0 0 0.75rem' }}>✨ {t('sorted_interests')}</p>}
          {loading ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_courses')}</p> :
            courses.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_courses_found')}</p> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {courses.map((c) => <CourseCard key={c.id} course={c} onOpen={setOpenSlug} />)}
              </div>
            )
          }
        </>
      )}

      {tab === 'mycourses' && <MyCoursesPanel />}
      {tab === 'enrollments' && <MyEnrollmentsPanel onOpen={setOpenSlug} />}
      {tab === 'wallet' && <WalletPanel />}

      {openSlug && <CourseDetailModal slug={openSlug} onClose={() => setOpenSlug(null)} onEnrolled={() => {}} />}
    </div>
  );
}
